import { VisuallyHidden } from '@piggybank/core';
import { AddAnother, Legend } from '@piggybank/form';
import get from 'lodash/get';
import keys from 'lodash/keys';
import last from 'lodash/last';
import values from 'lodash/values';
import T from 'prop-types';
import React from 'react';
import { DynamicFormField } from './DynamicFormField';
import { Optional, Translation, TranslationScope } from './context';
import {
  extendKeyPath,
  formNameFromKeyPath,
  getPageLevelSchemaPath,
  getSchemaPath,
  isCheckboxGroup as isCheckboxGroupFn,
  localizationKeyPath,
  matchDependantPropertyName,
  requiredArg,
} from './utils';

export const DynamicFormGroup = ({
  context = requiredArg('context'),
  formValues = requiredArg('formValues'),
  validate = requiredArg('validate'),
  schemaNode = requiredArg('schemaNode'),
  renderChildren = requiredArg('renderChildren'),
  setMetadataForFieldName,
  activeDependencies = [],
  pageLevelActiveDependencies = [],
  combinedProperties,
  getPageLevelErrors = requiredArg('getPageLevelErrors'),
}) => {
  const {
    keyPath = requiredArg('context.keyPath'),
    schemaPath = requiredArg('context.schemaPath'),
  } = context;

  if (schemaNode.type === 'array') {
    const fieldName = formNameFromKeyPath(keyPath).join('.');

    setMetadataForFieldName(fieldName, {
      context,
      componentName: 'DFGroup',
    });

    const groupFieldName = formNameFromKeyPath(context.keyPath);
    const path = localizationKeyPath(context.keyPath);
    const readOnly = get(formValues.readOnly, formNameFromKeyPath(keyPath));

    const shouldShowRemoveButton = index => {
      const readOnlyIndexArray = Array.isArray(readOnly)
        ? readOnly.map((_, index) => index)
        : [];

      return !readOnlyIndexArray.includes(index);
    };

    // itemInitialValue is required in Piggybank AddAnother
    const itemInitialValue =
      // The items prop can contain multiple fields
      get(schemaNode, 'items.type') === 'object'
        ? {}
        : // or a single field
          get(schemaNode, 'default', '');

    return (
      <>
        <TranslationScope path={path}>
          <Optional>
            <Legend>
              <Translation path="legend" />
            </Legend>
          </Optional>
          <Translation
            fallbackAbsolutePath="global.group.buttons"
            fallbackValue={{
              addButton: 'Add another',
              removeButton: 'Remove',
            }}
            path="buttons"
          >
            {({ addButton, removeButton }) => (
              <AddAnother
                fullWidth={get(schemaNode, 'fullWidth', false)}
                itemInitialValue={itemInitialValue}
                key={values(keyPath).join('-') + '-addAnother'}
                max={get(schemaNode, 'maxItems', Infinity)}
                min={get(schemaNode, 'minItems', 1)}
                minOnMount={get(schemaNode, 'minOnMount', 1)}
                name={fieldName}
                renderRemoveButtonText={({ index }) => (
                  <>
                    {removeButton}
                    <VisuallyHidden>{index + 1}</VisuallyHidden>
                  </>
                )}
                shouldShowRemoveButton={
                  readOnly && readOnly.length > 0
                    ? shouldShowRemoveButton
                    : null
                }
                textMap={{
                  addButton,
                  removeButton,
                }}
              >
                {({ index, path }) => (
                  <>
                    {renderChildren({
                      context: {
                        ...context,
                        keyPath: extendKeyPath(keyPath, {
                          index: last(path.split('.')),
                        }),
                        schemaPath: [...schemaPath, 'items'],
                        group: {
                          index,
                          groupFieldName,
                        },
                      },
                      formValues,
                    })}
                  </>
                )}
              </AddAnother>
            )}
          </Translation>
        </TranslationScope>
      </>
    );
  }

  if (schemaNode.type === 'object') {
    if (!combinedProperties) requiredArg('combinedProperties');

    const isCheckboxGroup = isCheckboxGroupFn(schemaNode);

    const children = keys(combinedProperties).reduce((acc, propertyName) => {
      const getDFGroupProps = (
        getSchemaPathForDFGroup,
        activeDependencies
      ) => ({
        context: {
          ...context,
          parent: 'DynamicFormGroup',
          keyPath: extendKeyPath(keyPath, {
            inputName: propertyName,
          }),
          schemaPath: getSchemaPathForDFGroup({
            schemaPath: context.schemaPath,
            propertyName,
            activeDependencies,
          }),
          isCheckboxGroup,
        },
        formValues,
      });

      return [
        ...acc,
        ...(matchDependantPropertyName(activeDependencies, propertyName) ||
        (schemaNode.properties &&
          typeof schemaNode.properties[propertyName] !== 'undefined') ||
        !schemaNode.properties
          ? [getDFGroupProps(getSchemaPath, activeDependencies)]
          : []),
        ...(matchDependantPropertyName(
          pageLevelActiveDependencies,
          propertyName
        )
          ? [
              getDFGroupProps(
                getPageLevelSchemaPath,
                pageLevelActiveDependencies
              ),
            ]
          : []),
      ];
    }, []);

    if (isCheckboxGroup) {
      const fieldName = formNameFromKeyPath(keyPath).join('.');

      setMetadataForFieldName(fieldName, { context });

      return (
        <DynamicFormField
          key={[...context.schemaPath, 'DynamicFormField']}
          {...{
            context: {
              ...context,
              parent: 'DynamicFormGroup',
            },
            formValues,
            validate,
            schemaNode,
            getPageLevelErrors,
            renderChildren: () =>
              children.map((child, index) => (
                <React.Fragment
                  key={`formGroup-isCheckboxGroup-child-${index}`}
                >
                  {renderChildren(child)}
                </React.Fragment>
              )),
          }}
        />
      );
    } else {
      return children.map((child, index) => (
        <React.Fragment key={`formGroup-child-${index}`}>
          {renderChildren(child)}
        </React.Fragment>
      ));
    }
  }

  throw new Error(
    "DynamicFormGroup was called with an item that isn't an array or object."
  );
};

DynamicFormGroup.propTypes = {
  context: T.shape({
    keyPath: T.object,
    schemaPath: T.arrayOf(T.oneOfType([T.string, T.number])),
  }),
  validate: T.func,
  formValues: T.object,
  renderChildren: T.func,
  schemaNode: T.object,
  setMetadataForFieldName: T.func,
  children: T.arrayOf(T.object),
  getPageLevelErrors: T.func,
  combinedProperties: T.object,
  activeDependencies: T.arrayOf(
    T.oneOfType([T.arrayOf(T.oneOfType([T.string, T.number])), T.object])
  ),
  pageLevelActiveDependencies: T.arrayOf(
    T.arrayOf(T.oneOfType([T.string, T.number]))
  ),
};
